"use client";

import React, { useState, useEffect } from "react";
import {
  FiFileText,
  FiLayers,
  FiBox,
  FiUsers,
  FiCheckCircle,
  FiHexagon,
  FiRepeat,
  FiGrid,
  FiBarChart2,
  FiCode,
  FiShare2,
  FiShield,
} from "react-icons/fi";

import ConnectButton from "../ConnectButton";
import ThemeToggle from "../ThemeToggle";
import styles from "./Navbar.module.css";

interface SubItem {
  text: string;
  href: string;
  icon: React.ReactNode;
}

interface NavItem {
  text: string;
  href?: string;
  icon?: React.ReactNode;
  subItems?: SubItem[];
}

const Navbar: React.FC = () => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const navItems: NavItem[] = [
    {
      text: "Blockchain",
      subItems: [
        { text: "Transactions", href: "/txs", icon: <FiFileText size={16} /> },
        {
          text: "Internal transactions",
          href: "/internal-txs",
          icon: <FiLayers size={16} />,
        },
        { text: "Blocks", href: "/blocks", icon: <FiBox size={16} /> },
        {
          text: "Top accounts",
          href: "/accounts",
          icon: <FiUsers size={16} />,
        },
        {
          text: "Verified contracts",
          href: "/verified-contracts",
          icon: <FiCheckCircle size={16} />,
        },
      ],
    },
    {
      text: "Tokens",
      subItems: [
        { text: "Tokens", href: "/tokens", icon: <FiHexagon size={16} /> },
        {
          text: "Token transfers",
          href: "/token-transfers",
          icon: <FiRepeat size={16} />,
        },
      ],
    },
    {
      text: "DApps",
      href: "/apps",
      icon: <FiGrid size={16} />,
    },
    {
      text: "Charts & stats",
      href: "/stats",
      icon: <FiBarChart2 size={16} />,
    },
    {
      text: "API",
      subItems: [
        { text: "REST API", href: "/api-docs", icon: <FiCode size={16} /> },
        { text: "GraphQL", href: "/graphiql", icon: <FiShare2 size={16} /> },
      ],
    },
    {
      text: "Other",
      subItems: [
        {
          text: "Verify contract",
          href: "/contract-verification",
          icon: <FiShield size={16} />,
        },
      ],
    },
  ];

  return (
    <nav className={styles.navbar}>
      <div className={styles.container}>
        <div className={styles.logo}>
          <a href="/">
            <img
              src={"/assets/network_logo_dark.webp"}
              alt="OpenGPU"
              className={styles.logoImage}
            />
          </a>
        </div>

        <ul className={styles.navList}>
          {navItems.map((item) => (
            <li
              key={item.text}
              className={styles.navItem}
              onMouseEnter={() => item.subItems && setOpenDropdown(item.text)}
              onMouseLeave={() => item.subItems && setOpenDropdown(null)}
            >
              {item.href ? (
                <a href={item.href} className={styles.navLink}>
                  {item.text}
                </a>
              ) : (
                <button
                  className={`${styles.navLink} ${openDropdown === item.text ? styles.navLinkActive : ""}`}
                  type="button"
                >
                  {item.text}
                  {item.subItems && <span className={styles.arrow}>▼</span>}
                </button>
              )}

              {item.subItems && (
                <div
                  className={`${styles.dropdown} ${openDropdown === item.text ? styles.dropdownVisible : ""}`}
                >
                  {item.subItems.map((subItem) => (
                    <a
                      key={subItem.text}
                      href={subItem.href}
                      className={styles.dropdownItem}
                    >
                      <span className={styles.dropdownItemIcon}>
                        {subItem.icon}
                      </span>
                      {subItem.text}
                    </a>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className={styles.rightActions}>
          <ThemeToggle />
          <ConnectButton />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
